import { Box, Button, Popover, Typography, useTheme } from '@mui/material';
import { FolderPlus, PencilSimpleLine, Trash } from '@phosphor-icons/react';
import React from 'react';
import { Folder } from '../../../../util/Types/Folder';

interface FolderItemMenuProps {
  folder: Folder;
  anchorEl: HTMLElement;
  onClose: () => void;
  onRename?: (folder: Folder) => void;
  onDelete?: (folder: Folder) => void;
  canAddSubfolder?: boolean;
  onAddSubfolder?: (folder: Folder) => void;
}

export const FolderItemMenu: React.FC<FolderItemMenuProps> = ({
  folder,
  anchorEl,
  onClose,
  onRename,
  onDelete,
  canAddSubfolder = false,
  onAddSubfolder,
}) => {
  const theme = useTheme();
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: { marginLeft: '8px' },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: '8px',
          background: isDarkMode ? palette.gray[900] : '#FFFFFF',
          minWidth: '180px',
        }}
      >
        {canAddSubfolder && onAddSubfolder && (
          <Button
            variant='contained'
            onClick={() => {
              onClose();
              onAddSubfolder(folder);
            }}
            sx={{
              width: '100%',
              background: 'unset',
              justifyContent: 'flex-start',
              textTransform: 'unset',
              fontSize: '16px',
              boxShadow: 'unset',
              gap: '16px',
              px: '8px',
              color: isDarkMode ? palette.gray[200] : palette.gray[700],
              '&:hover': {
                background: isDarkMode ? palette.gray[700] : palette.gray[100],
                boxShadow: 'unset',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <FolderPlus
                size={20}
                color={isDarkMode ? palette.gray[400] : palette.gray[500]}
              />
              <Typography
                sx={{
                  color: isDarkMode ? palette.gray[200] : palette.gray[700],
                }}
              >
                New subfolder
              </Typography>
            </Box>
          </Button>
        )}
        {onRename && (
          <Button
            variant='contained'
            onClick={() => {
              onClose();
              onRename(folder);
            }}
            sx={{
              width: '100%',
              background: 'unset',
              justifyContent: 'flex-start',
              textTransform: 'unset',
              fontSize: '16px',
              boxShadow: 'unset',
              gap: '16px',
              px: '8px',
              color: isDarkMode ? palette.gray[200] : palette.gray[700],
              '&:hover': {
                background: isDarkMode ? palette.gray[700] : palette.gray[100],
                boxShadow: 'unset',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <PencilSimpleLine
                size={20}
                color={isDarkMode ? palette.gray[400] : palette.gray[500]}
              />
              <Typography
                sx={{
                  color: isDarkMode ? palette.gray[200] : palette.gray[700],
                }}
              >
                Update Folder
              </Typography>
            </Box>
          </Button>
        )}
        {onDelete && (
          <Button
            variant='contained'
            onClick={() => {
              onClose();
              onDelete(folder);
            }}
            sx={{
              width: '100%',
              background: 'unset',
              justifyContent: 'flex-start',
              gap: '16px',
              textTransform: 'unset',
              fontSize: '16px',
              boxShadow: 'unset',
              color: '#b91c1c',
              px: '8px',
              '&:hover': {
                background: '#fecdd3',
                boxShadow: 'unset',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <Trash size={20} color='#b91c1c' />
              <Typography>Delete</Typography>
            </Box>
          </Button>
        )}
      </Box>
    </Popover>
  );
};
